
import React from 'react';
import { Link } from 'react-router-dom';

const BillwaveSettingsGuide = () => {
  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
        使い方 - Billwave設定
      </h1>
      <p className="text-lg text-gray-800 mb-6">
        「Billwave設定」では、Billwaveの利用に必要なキーやIDの登録・変更・削除を行うことができます。
      </p>

      <hr className="my-8 border-gray-200" />

      {/* Section 1: Stripe API Key Settings */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Stripe APIキー設定</h2>
        <p className="mb-4 text-gray-700">
          Stripeと連携するためのAPIキーを設定・保存します。
        </p>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Stripe APIキーの設定方法</h3>
        <ol className="list-decimal list-inside space-y-2 mb-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
          <li>StripeでAPIキーを作成し、キーをコピーしてください。</li>
          <li>「Stripeシークレットキー」欄に貼り付け、「APIキーを保存」ボタンをクリックします。</li>
          <li>ステータスが「設定済み」と表示されれば、設定は完了です。</li>
        </ol>

        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-800 p-4" role="alert">
          <p className="font-bold">ヒント</p>
          <p className="mt-2">Stripe APIキーは非常に重要な情報です。<br />Billwaveではセキュリティ確保のため、APIキーは暗号化して保存・管理しています。<br />そのため、一度登録したAPIキーの値を後から確認することはできません。</p>
          <p className="mt-2">APIキーが不明になった場合は、Stripe側で新しいAPIキーを発行（ローテーション）し、再設定してください。</p>
        </div>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section 2: Invoice Settings */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">請求書設定</h2>
        <p className="mb-4 text-gray-700">
          Billwaveでは、Stripe請求書に表示される「請求書メモ」と「請求書フッター」を設定できます。
        </p>
        <p className="mb-6 text-gray-700">
          設定した内容は、Billwaveから発行するすべてのStripe請求書（CSVアップロードによる一括発行、即時発行、予約発行）に適用されます。
        </p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">設定方法</h3>
        <ol className="list-decimal list-inside space-y-2 mb-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
          <li>ユーザーメニューの 「Billwave設定」 を開きます。</li>
          <li>「請求書設定」にある「請求書メモ」と「請求書フッター」を入力します。</li>
          <li>「保存」 をクリックします。</li>
        </ol>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">各項目について</h3>
        <div className="space-y-4 mb-6">
          <div>
            <h4 className="font-bold text-gray-800">請求書メモ</h4>
            <p className="text-gray-700">請求書の上部に表示されるメッセージです。</p>
            <div className="bg-gray-50 p-3 rounded mt-1 border border-gray-200">
              <p className="text-sm font-semibold text-gray-500 mb-1">例</p>
              <p className="text-gray-700 whitespace-pre-wrap">
                いつもありがとうございます。{"\n\n"}
                今月分のご請求となります。{"\n"}
                内容をご確認のうえ、お支払いをお願いいたします。
              </p>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-gray-800">請求書フッター</h4>
            <p className="text-gray-700">請求書の下部に表示されるメッセージです。</p>
            <div className="bg-gray-50 p-3 rounded mt-1 border border-gray-200">
              <p className="text-sm font-semibold text-gray-500 mb-1">例</p>
              <p className="text-gray-700 whitespace-pre-wrap">
                ご不明な点がございましたら、お気軽にお問い合わせください。{"\n\n"}
                株式会社○○{"\n"}
                TEL：00-0000-0000{"\n"}
                Mail：support@example.com
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 text-gray-700">
          <div>
            <h4 className="font-bold text-gray-800">改行について</h4>
            <p>入力した改行は、そのまま請求書へ反映されます。</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-800">未設定の場合</h4>
            <p>請求書メモ・請求書フッターを空欄のまま保存した場合は、Billwaveから値を送信しません。</p>
            <p>そのため、Stripeダッシュボードで設定されているデフォルトの請求書メモ・フッターが自動的に使用されます。</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-800">適用タイミング</h4>
            <p>設定内容は、保存後に新しく作成される請求書から反映されます。</p>
            <p>すでに発行済みの請求書には適用されません。</p>
          </div>
        </div>
      </section>

      <hr className="my-8 border-gray-200" />

      {/* Section 3: Tax Rate Settings */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">税率設定</h2>
        <p className="mb-4 text-gray-700">
          税率設定では、請求書アップロード時に使用する「税率ID」を設定できます。
        </p>
        <p className="mb-6 text-gray-700">
          税率IDが未設定の場合、消費税を含む請求書を発行することができません。<br />
          必要に応じて、事前に税率IDを設定してください。
        </p>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">税率設定方法</h3>
        <ol className="list-decimal list-inside space-y-2 bg-gray-50 p-6 rounded-lg border border-gray-200">
          <li>税率IDをStripeのダッシュボードで確認します。</li>
          <li>「行を追加」ボタンをクリックしてください。</li>
          <li>Stripe税率ID (<code className="bg-gray-200 px-1 rounded text-sm">tax_rate_id</code>)欄に税率IDを入力、保存ボタンをクリックしてください。</li>
          <li>操作欄に「保存済み」と表示されれば設定は完了です。</li>
        </ol>
      </section>

      <div className="mt-8 text-center">
        <Link 
          to="/support/how-to-use"
          className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-colors"
        >
          「使い方」一覧へ戻る
        </Link>
      </div>

    </div>
  );
};

export default BillwaveSettingsGuide;
